/**
 * UK delivery postcode district helpers for Profit Postcode rollup (Phase 1C).
 *
 * District = outward code (e.g. LS10 from LS10 1AB). Blank / unparseable → Unknown
 * so depot-fallback and consEntered-only orders stay visible (Bloomberg / Dual View).
 *
 * MUST STAY IN SYNC with `zeecore/src/projections/profitability/postcodeDistrict.ts`
 * (SPA Profit tab vs ZOE get_postcode_profitability_summary).
 */

const UNKNOWN_DISTRICT = 'Unknown';

/** Normalise for parsing: uppercase, strip all whitespace. */
export function normalisePostcodeCompact(raw: string | null | undefined): string {
  return String(raw ?? '').replace(/\s+/g, '').toUpperCase().trim();
}

/**
 * Outward code (postcode district) for grouping.
 * Full UK postcodes end with an inward unit: digit + two letters (e.g. 1AB).
 * Already-short values (outward only, e.g. LS10 / M1) are returned as-is.
 */
export function postcodeDistrict(raw: string | null | undefined): string {
  const compact = normalisePostcodeCompact(raw);
  if (!compact) return UNKNOWN_DISTRICT;

  // Reject obvious non-postcodes (depot names, free text).
  if (!/^[A-Z]{1,2}\d/.test(compact)) return UNKNOWN_DISTRICT;

  // Full postcode: strip inward (NLL). Outward is 2–4 chars.
  const full = compact.match(/^([A-Z]{1,2}\d[A-Z\d]?)\d[A-Z]{2}$/);
  if (full?.[1]) return full[1];

  // Already a district / short sector (no inward unit).
  if (/^[A-Z]{1,2}\d[A-Z\d]?$/.test(compact)) return compact;

  return UNKNOWN_DISTRICT;
}

export function isUnknownPostcodeDistrict(district: string): boolean {
  return district === UNKNOWN_DISTRICT;
}

/**
 * Display label: `LS10 · Leeds` when a city is known; plain district otherwise.
 */
export function formatPostcodeDistrictLabel(
  district: string,
  city?: string | null,
): string {
  if (isUnknownPostcodeDistrict(district)) return UNKNOWN_DISTRICT;
  const place = String(city ?? '').trim();
  if (!place || place.toLowerCase() === 'uk') return district;
  return `${district} · ${place}`;
}

export { UNKNOWN_DISTRICT };
